import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { NGP_LOGO_BASE64 } from '../constants/ngp-logo.constant';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  VerticalAlign,
  BorderStyle,
  Header,
  Footer,
  PageNumber,
  ImageRun,
  PageOrientation,
  TableLayoutType
} from 'docx';

@Injectable({
  providedIn: 'root',
})
export class ExportService {
  constructor() {}

  /**
   * Export data to Excel
   * @param data - The array of objects to export
   * @param fileName - The name of the Excel file (optional, default: 'ExportedData.xlsx')
   * @param sheetName - The name of the worksheet (optional, default: 'Sheet1')
   */
  exportToExcel(data: any[], fileName: string = 'ExportedData.xlsx', sheetName: string = 'Sheet1', colWidths?: any[]): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    // Convert JSON data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    if (colWidths && colWidths.length) {
      worksheet['!cols'] = colWidths;
    }

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate the Excel file buffer
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob from the buffer and save as file
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /**
   * Export data to CSV
   * @param data - The array of objects to export
   * @param fileName - The name of the CSV file
   */
  exportToCSV(data: any[], fileName: string = 'ExportedData.csv'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, fileName.endsWith('.csv') ? fileName : `${fileName}.csv`);
  }

  /**
   * Export data directly to PDF file download
   * @param data - The array of objects to export
   * @param fileName - The name of the PDF file
   * @param title - The title of the document
   */
  exportToPDF(data: any[], fileName: string = 'ExportedData.pdf', title: string = 'Exported Report'): void {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
      });

      const headers = Object.keys(data[0]);
      const rows = data.map(item =>
        headers.map(header => item[header] !== undefined && item[header] !== null ? String(item[header]) : '')
      );

      // Title & Metadata
      doc.setFontSize(14);
      doc.setTextColor(30, 41, 59);
      doc.text(title, 40, 35);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(`Generated on ${new Date().toLocaleString()}`, 40, 50);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 60,
        styles: {
          fontSize: 7,
          cellPadding: 3,
          overflow: 'linebreak'
        },
        headStyles: {
          fillColor: [37, 99, 235],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        theme: 'grid'
      });

      const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      doc.save(pdfFileName);
    } catch (e) {
      console.error('jsPDF export error, falling back to print window:', e);
      const headers = Object.keys(data[0]);
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups to export PDF.');
        return;
      }

      const rowsHtml = data.map(row => `
        <tr>
          ${headers.map(h => `<td style="border: 1px solid #e2e8f0; padding: 7px 6px; font-size: 10px;">${row[h] !== undefined && row[h] !== null ? row[h] : ''}</td>`).join('')}
        </tr>
      `).join('');

      const headersHtml = headers.map(h => `<th style="border: 1px solid #cbd5e1; background-color: #2563eb; color: #ffffff; padding: 8px 6px; font-size: 10px; text-align: left; white-space: nowrap;">${h}</th>`).join('');

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; margin: 15px; color: #1e293b; }
            h2 { color: #1e293b; font-size: 18px; margin-bottom: 4px; }
            p.subtitle { color: #64748b; font-size: 11px; margin-top: 0; margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            tr:nth-child(even) { background-color: #f8fafc; }
            @media print {
              body { margin: 0; }
              @page { size: landscape; margin: 8mm; }
            }
          </style>
        </head>
        <body>
          <h2>${title}</h2>
          <p class="subtitle">Generated on ${new Date().toLocaleString()}</p>
          <table>
            <thead>
              <tr>${headersHtml}</tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    }
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form (QF sheet)
   */
  async exportToNgpQuotationForm(
    rows: any[],
    fileName: string = 'NGP_Quotation_Form.xlsx',
    options: { reviewerName?: string; dateStr?: string } = {}
  ): Promise<void> {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const reviewerName = (options.reviewerName || 'KAVYA').trim().toUpperCase();
    const dateStr = options.dateStr || (() => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('QF', {
      views: [{ showGridLines: true }]
    });

    // 1. Column Widths (A to N)
    worksheet.columns = [
      { width: 8 },  // A: SL NO
      { width: 18 }, // B: PART NUMBER
      { width: 22 }, // C: QUOTED PART
      { width: 34 }, // D: DESCRIPTION
      { width: 8 },  // E: UOM
      { width: 12 }, // F: QUANTITY
      { width: 14 }, // G: MOQ/SPQ
      { width: 14 }, // H: AVAILABILITY
      { width: 14 }, // I: UNIT PRICE
      { width: 14 }, // J: TOTAL PRICE
      { width: 12 }, // K: LEAD TIME
      { width: 20 }, // L: MAKE
      { width: 10 }, // M: COO
      { width: 16 }, // N: REMARKS
    ];

    // Set heights for top rows
    worksheet.getRow(1).height = 22;
    worksheet.getRow(2).height = 22;
    worksheet.getRow(3).height = 22;
    worksheet.getRow(4).height = 28;

    const thinBorder: Partial<ExcelJS.Borders> = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } }
    };

    // 2. Logo in A1:B3
    worksheet.mergeCells('A1:B3');
    try {
      const logoImageId = workbook.addImage({
        base64: NGP_LOGO_BASE64,
        extension: 'jpeg'
      });
      worksheet.addImage(logoImageId, {
        tl: { col: 0.05, row: 0.05 },
        br: { col: 1.95, row: 2.95 },
        editAs: 'oneCell'
      } as any);
    } catch (e) {
      console.warn('Could not add logo image:', e);
    }
    const a1Cell = worksheet.getCell('A1');
    a1Cell.value = 'NGP SOLUTIONS';
    a1Cell.alignment = { vertical: 'middle', horizontal: 'center' };
    ['A1', 'A2', 'A3', 'B1', 'B2', 'B3'].forEach(cellRef => {
      worksheet.getCell(cellRef).border = thinBorder;
    });

    // 3. Header Titles C1:K1 and C2:K3
    worksheet.mergeCells('C1:K1');
    const c1Cell = worksheet.getCell('C1');
    c1Cell.value = 'NGP SOLUTIONS';
    c1Cell.font = { name: 'Calibri', size: 14, bold: true, color: { argb: 'FF000000' } };
    c1Cell.alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('C2:K3');
    const c2Cell = worksheet.getCell('C2');
    c2Cell.value = 'ENQUIRY REVIEW AND QUOTATION FORM';
    c2Cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF000000' } };
    c2Cell.alignment = { vertical: 'middle', horizontal: 'center' };

    // 4. Quotation Reviewed By (L1:L3)
    worksheet.mergeCells('L1:L3');
    const l1Cell = worksheet.getCell('L1');
    l1Cell.value = `QUOTATION\nREVIEWED\nBY ${reviewerName}`;
    l1Cell.font = { name: 'Calibri', size: 9, bold: true, color: { argb: 'FF000000' } };
    l1Cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    ['L1', 'L2', 'L3'].forEach(c => worksheet.getCell(c).border = thinBorder);

    // 5. Document Metadata (M1:N3)
    const m1 = worksheet.getCell('M1');
    m1.value = 'Doc.Ref.';
    m1.font = { name: 'Calibri', size: 9, bold: true };
    m1.alignment = { vertical: 'middle', horizontal: 'left' };
    m1.border = thinBorder;

    const n1 = worksheet.getCell('N1');
    n1.value = 'NGP-MKT-QF-02';
    n1.font = { name: 'Calibri', size: 9, bold: true };
    n1.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    n1.border = thinBorder;

    const m2 = worksheet.getCell('M2');
    m2.value = 'Revision\nNo.';
    m2.font = { name: 'Calibri', size: 9, bold: true };
    m2.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
    m2.border = thinBorder;

    const n2 = worksheet.getCell('N2');
    n2.value = '00';
    n2.font = { name: 'Calibri', size: 9, bold: true };
    n2.alignment = { vertical: 'middle', horizontal: 'center' };
    n2.border = thinBorder;

    const m3 = worksheet.getCell('M3');
    m3.value = 'Date';
    m3.font = { name: 'Calibri', size: 9, bold: true };
    m3.alignment = { vertical: 'middle', horizontal: 'left' };
    m3.border = thinBorder;

    const n3 = worksheet.getCell('N3');
    n3.value = dateStr;
    n3.font = { name: 'Calibri', size: 9, bold: true };
    n3.alignment = { vertical: 'middle', horizontal: 'center' };
    n3.border = thinBorder;

    // 6. Table Headers (Row 4)
    const blueHeaders = [
      'SL NO', 'PART NUMBER', 'QUOTED PART', 'DESCRIPTION', 'UOM',
      'QUANTITY', 'MOQ/SPQ', 'AVAILABILITY', 'UNIT PRICE', 'TOTAL PRICE',
      'LEAD TIME', 'MAKE', 'COO', 'REMARKS'
    ];

    const row4 = worksheet.getRow(4);
    blueHeaders.forEach((h, idx) => {
      const colIdx = idx + 1;
      const cell = row4.getCell(colIdx);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF002060' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF001040' } },
        left: { style: 'thin', color: { argb: 'FF001040' } },
        bottom: { style: 'thin', color: { argb: 'FF001040' } },
        right: { style: 'thin', color: { argb: 'FF001040' } }
      };
    });

    // 7. Populate Data Rows (Row 5 onwards)
    const dataBorder: Partial<ExcelJS.Borders> = {
      top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
      left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
      bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
      right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
    };

    rows.forEach((row, i) => {
      const r = 5 + i;
      const dataRow = worksheet.getRow(r);
      dataRow.height = 20;

      const qty = Number(row.quantity ?? row.projectQty ?? 0);
      const unitPrice = Number(row.unitPrice ?? 0);

      // Col A: SL NO
      const cA = dataRow.getCell(1);
      cA.value = row.slNo ?? (i + 1);
      cA.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col B: PART NUMBER
      const cB = dataRow.getCell(2);
      cB.value = row.partNumber ?? '';
      cB.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col C: QUOTED PART
      const cC = dataRow.getCell(3);
      cC.value = row.quotedPart ?? '';
      cC.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col D: DESCRIPTION
      const cD = dataRow.getCell(4);
      cD.value = row.description ?? '';
      cD.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col E: UOM
      const cE = dataRow.getCell(5);
      cE.value = row.uom ?? 'EA';
      cE.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col F: QUANTITY
      const cF = dataRow.getCell(6);
      cF.value = qty;
      cF.numFmt = '#,##0';
      cF.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col G: MOQ/SPQ
      const cG = dataRow.getCell(7);
      cG.value = row.moqSpq !== undefined && row.moqSpq !== null ? String(row.moqSpq) : '';
      cG.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col H: AVAILABILITY
      const cH = dataRow.getCell(8);
      cH.value = row.availability !== undefined && row.availability !== null ? String(row.availability) : '';
      cH.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col I: UNIT PRICE
      const cI = dataRow.getCell(9);
      cI.value = unitPrice;
      cI.numFmt = '#,##0.00';
      cI.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col J: TOTAL PRICE
      const cJ = dataRow.getCell(10);
      cJ.value = {
        formula: `F${r}*I${r}`,
        result: qty * unitPrice
      };
      cJ.numFmt = '#,##0.00';
      cJ.alignment = { vertical: 'middle', horizontal: 'right' };

      // Col K: LEAD TIME
      const cK = dataRow.getCell(11);
      cK.value = row.leadTime !== undefined && row.leadTime !== null ? String(row.leadTime) : '';
      cK.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col L: MAKE
      const cL = dataRow.getCell(12);
      cL.value = row.make ?? '';
      cL.alignment = { vertical: 'middle', horizontal: 'left' };

      // Col M: COO
      const cM = dataRow.getCell(13);
      cM.value = row.coo ?? '';
      cM.alignment = { vertical: 'middle', horizontal: 'center' };

      // Col N: REMARKS
      const cN = dataRow.getCell(14);
      cN.value = row.remarks ?? '';
      cN.alignment = { vertical: 'middle', horizontal: 'left' };

      // Apply borders & font to all columns (1 to 14) in this row
      for (let c = 1; c <= 14; c++) {
        const cell = dataRow.getCell(c);
        cell.border = dataBorder;
        cell.font = { name: 'Calibri', size: 10 };
      }
    });

    // 8. Terms & Conditions section below the data rows
    let currentTermRow = 5 + rows.length + 1; // 1 blank row spacing after table

    // Header for Terms & Conditions
    worksheet.mergeCells(`B${currentTermRow}:N${currentTermRow}`);
    const tcHeaderCell = worksheet.getCell(`B${currentTermRow}`);
    tcHeaderCell.value = 'Terms & Conditions:';
    tcHeaderCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF002060' } };
    tcHeaderCell.alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getRow(currentTermRow).height = 24;
    currentTermRow++;

    const termsAndConditions: string[] = [
      'GST will be applied at the time of billing.',
      'Costing in this quotation is prepared as per the existing customs tariff, duties, taxes, and statutory laws/notifications. Any changes in government notifications prior to delivery will impact the product cost.',
      'Any applicable law changes will affect the quoted product price.',
      'Warranty as per manufacturer.',
      'Please review MOQ, quoted UOM, part number, and lead time carefully before placing an order. Alternate product suggestions are based on market availability only; the buyer holds full responsibility for technical decisions.',
      'Distributor/Manufacturer COC will be provided with the supply of goods.',
      'All cables, braids, heat shrink, sleeves, fasteners, consumables, etc. are subject to ±10% variation or actuals at billing, depending on packing/spool conditions.',
      'For chemicals/DG goods, please check the shelf-life column carefully before ordering.',
      'All quotes and orders are NCNR – Non-Cancellable and Non-Returnable.',
      'All product parts are subject to stock availability.',
      'Any increase or decrease in quoted quantity will affect the unit cost.',
      'All parts are subject to 100% re-stocking charges.',
      'Prices are quoted based on current USD/EUR/GBP to INR conversion. Any fluctuation beyond 2% at the time of PO will impact the unit cost.',
      'Orders below ₹3,000/- will attract a delivery charge of ₹300/- in the invoice.',
      'Purchase Orders must be placed in the name of:\nNGP SOLUTIONS\nJR Urbania Prime, #969 & 970, Lingapura Village, Anekal Taluk, Bangalore – 562106\nGST : 29BCWPD5012J1ZC',
      'Payment Terms : Terms will vary depending on the customer.',
      'Generally, the company approves Net 30 terms subject to finance approval.',
      'As per company policy, for any new customers, 100% advance payment is required until either: The transaction value reaches ₹10 lakhs, or The customer submits 2 years of audited financial statements for review and approval of credit terms.'
    ];

    termsAndConditions.forEach(term => {
      worksheet.mergeCells(`B${currentTermRow}:N${currentTermRow}`);
      const termCell = worksheet.getCell(`B${currentTermRow}`);
      termCell.value = `▪  ${term}`;
      termCell.font = { name: 'Calibri', size: 9.5, color: { argb: 'FF1E293B' } };
      termCell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };

      const lineCount = (term.match(/\n/g) || []).length + 1;
      if (lineCount > 1) {
        worksheet.getRow(currentTermRow).height = lineCount * 18 + 10;
      } else if (term.length > 140) {
        worksheet.getRow(currentTermRow).height = 32;
      } else {
        worksheet.getRow(currentTermRow).height = 20;
      }

      currentTermRow++;
    });

    // 9. Configure print settings: A4 landscape, fit to 1 page wide
    worksheet.pageSetup = {
      orientation: 'landscape',
      paperSize: 9, // A4
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.3,
        right: 0.3,
        top: 0.4,
        bottom: 0.4,
        header: 0.2,
        footer: 0.2
      }
    };

    // 10. Write buffer and trigger browser download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form in PDF format with Terms & Conditions
   */
  exportToNgpQuotationPdf(
    rows: any[],
    fileName: string = 'NGP_Quotation.pdf',
    options: { reviewerName?: string; dateStr?: string; projectName?: string } = {}
  ): void {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const reviewerName = (options.reviewerName || 'KAVYA').trim().toUpperCase();
    const dateStr = options.dateStr || (() => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();
    const projectName = options.projectName || '';

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 28;

    // 1. Header: Logo
    try {
      doc.addImage(NGP_LOGO_BASE64, 'JPEG', margin, 18, 65, 38);
    } catch (e) {
      console.warn('Could not add logo to PDF:', e);
    }

    // 2. Header: Title & Subtitle (Center)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(0, 32, 96); // #002060
    doc.text('NGP SOLUTIONS', pageWidth / 2, 30, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.setTextColor(51, 65, 85);
    doc.text('ENQUIRY REVIEW AND QUOTATION FORM', pageWidth / 2, 45, { align: 'center' });

    if (projectName) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(projectName, pageWidth / 2, 57, { align: 'center' });
    }

    // 3. Header: Metadata Box (Right)
    const metaBoxX = pageWidth - margin - 150;
    const metaBoxY = 16;
    const metaBoxW = 150;
    const metaBoxH = 46;

    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(71, 85, 105);
    doc.text('Doc. Ref.:', metaBoxX + 6, metaBoxY + 12);
    doc.setFont('helvetica', 'normal');
    doc.text('NGP-MKT-QF-02', metaBoxX + 55, metaBoxY + 12);

    doc.setFont('helvetica', 'bold');
    doc.text('Revision:', metaBoxX + 6, metaBoxY + 22);
    doc.setFont('helvetica', 'normal');
    doc.text('00', metaBoxX + 55, metaBoxY + 22);

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', metaBoxX + 6, metaBoxY + 32);
    doc.setFont('helvetica', 'normal');
    doc.text(dateStr, metaBoxX + 55, metaBoxY + 32);

    doc.setFont('helvetica', 'bold');
    doc.text('Reviewed By:', metaBoxX + 6, metaBoxY + 42);
    doc.setFont('helvetica', 'normal');
    doc.text(reviewerName, metaBoxX + 62, metaBoxY + 42);

    // Calculate financial totals
    let subtotal = 0;
    rows.forEach(r => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const rowTotal = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);
      subtotal += rowTotal;
    });

    const cgst = subtotal * 0.09;
    const sgst = subtotal * 0.09;
    const grandTotal = subtotal + cgst + sgst;

    // 4. AutoTable Data (15 Columns matching Quotation Template)
    const tableHeaders = [
      'SL', 'Customer Ref.', 'Manufacturer Part No.', 'Alternate Quoted', 'Description',
      'Make', 'HSN', 'UOM', 'Qty', 'MOQ/SPQ',
      'Unit Price', 'Total Price', 'Lead Time', 'COO', 'Remarks'
    ];

    const formatCurrency = (val: any) => {
      const num = Number(val);
      return isNaN(num) || num <= 0 ? '-' : num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const tableData = rows.map((r, idx) => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const total = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);

      return [
        r.slNo ?? (idx + 1),
        r.partNumber || '-',
        r.quotedPart || '-',
        r.alternateQuoted || '-',
        r.description || '-',
        r.make || '-',
        r.hsnCode || '-',
        r.uom || 'EA',
        qty ? qty.toLocaleString('en-IN') : '0',
        r.moqSpq || '-',
        formatCurrency(unitPrice),
        formatCurrency(total),
        r.leadTime || '-',
        r.coo || '-',
        r.remarks || '-'
      ];
    });

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 68,
      margin: { left: margin, right: margin, bottom: 25 },
      styles: {
        font: 'helvetica',
        fontSize: 6.8,
        cellPadding: 2.5,
        overflow: 'linebreak',
        textColor: [0, 0, 0],
        lineColor: [203, 213, 225],
        lineWidth: 0.5
      },
      headStyles: {
        fillColor: [0, 32, 96],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 18 },
        1: { halign: 'left', cellWidth: 55 },
        2: { halign: 'left', cellWidth: 68 },
        3: { halign: 'left', cellWidth: 52 },
        4: { halign: 'left' },
        5: { halign: 'left', cellWidth: 55 },
        6: { halign: 'center', cellWidth: 34 },
        7: { halign: 'center', cellWidth: 22 },
        8: { halign: 'right', cellWidth: 32 },
        9: { halign: 'center', cellWidth: 38 },
        10: { halign: 'right', cellWidth: 50 },
        11: { halign: 'right', cellWidth: 54 },
        12: { halign: 'center', cellWidth: 36 },
        13: { halign: 'center', cellWidth: 24 },
        14: { halign: 'left', cellWidth: 46 }
      },
      theme: 'grid',
      didDrawPage: () => {
        const pageNum = doc.getNumberOfPages();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text('NGP SOLUTIONS - Confidential Customer Quotation', margin, pageHeight - 12);
        doc.text(`Page ${pageNum}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
      }
    });

    // 5. Financial Summary Table (Right-aligned, yellow highlight)
    let finalY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 12 : 100;
    if (finalY + 80 > pageHeight - 30) {
      doc.addPage();
      finalY = 30;
    }

    const summaryW = 180;
    const summaryX = pageWidth - margin - summaryW;
    const rowH = 14;

    const summaryRows = [
      { label: 'Total :', value: subtotal > 0 ? subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: true },
      { label: 'CGST @ 9%', value: cgst > 0 ? cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: false },
      { label: 'SGST @ 9%', value: sgst > 0 ? sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: false },
      { label: 'IGST @ 18%', value: '-', isYellow: false },
      { label: 'Grand Total :', value: grandTotal > 0 ? grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', isYellow: true }
    ];

    summaryRows.forEach((sr, idx) => {
      const curY = finalY + (idx * rowH);
      if (sr.isYellow) {
        doc.setFillColor(255, 255, 0); // Yellow
      } else {
        doc.setFillColor(255, 255, 255);
      }
      doc.rect(summaryX, curY, summaryW, rowH, 'FD');

      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.rect(summaryX, curY, summaryW / 2, rowH, 'S');
      doc.rect(summaryX + summaryW / 2, curY, summaryW / 2, rowH, 'S');

      doc.setFont('helvetica', sr.isYellow ? 'bold' : 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(0, 0, 0);
      doc.text(sr.label, summaryX + 6, curY + 9.5);
      doc.text(sr.value, summaryX + summaryW - 6, curY + 9.5, { align: 'right' });
    });

    finalY += (summaryRows.length * rowH) + 12;

    // 6. Quotation Notes Box
    if (finalY + 45 > pageHeight - 30) {
      doc.addPage();
      finalY = 30;
    }
    const notesW = pageWidth - (margin * 2);
    doc.setDrawColor(100, 116, 139);
    doc.setLineWidth(0.5);
    doc.rect(margin, finalY, notesW, 30, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 32, 96);
    doc.text('Quotation Notes:', margin + 6, finalY + 11);

    finalY += 40;

    // 7. Terms & Conditions section below table
    if (finalY + 130 > pageHeight - 30) {
      doc.addPage();
      finalY = 32;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 32, 96);
    doc.text('Terms & Conditions:', margin, finalY);

    doc.setDrawColor(0, 32, 96);
    doc.setLineWidth(0.8);
    doc.line(margin, finalY + 3, margin + 110, finalY + 3);

    finalY += 12;

    const termsAndConditions: string[] = this.getNgpTermsAndConditions();

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(30, 41, 59);

    const maxTextWidth = pageWidth - (margin * 2) - 15;

    termsAndConditions.forEach(term => {
      const splitText = doc.splitTextToSize(term, maxTextWidth);
      const textHeight = splitText.length * 8.5;

      if (finalY + textHeight > pageHeight - 25) {
        doc.addPage();
        finalY = 32;
      }

      // Small square bullet
      doc.setFillColor(0, 32, 96);
      doc.rect(margin + 2, finalY - 4, 2.5, 2.5, 'F');

      doc.text(splitText, margin + 10, finalY);
      finalY += textHeight + 2;
    });

    const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    doc.save(pdfFileName);
  }

  /**
   * Convert base64 data string to Uint8Array for docx ImageRun
   */
  private base64ToUint8Array(base64: string): Uint8Array {
    try {
      const cleanBase64 = base64.replace(/^data:image\/\w+;base64,/, '').trim();
      const binaryString = window.atob(cleanBase64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    } catch (e) {
      console.warn('Failed to convert base64 to Uint8Array:', e);
      return new Uint8Array(0);
    }
  }

  /**
   * Standard NGP terms and conditions list
   */
  private getNgpTermsAndConditions(): string[] {
    return [
      'GST will be applied at the time of billing.',
      'Costing in this quotation is prepared as per the existing customs tariff, duties, taxes, and statutory laws/notifications. Any changes in government notifications prior to delivery will impact the product cost.',
      'Any applicable law changes will affect the quoted product price.',
      'Warranty as per manufacturer.',
      'Please review MOQ, quoted UOM, part number, and lead time carefully before placing an order. Alternate product suggestions are based on market availability only; the buyer holds full responsibility for technical decisions.',
      'Distributor/Manufacturer COC will be provided with the supply of goods.',
      'All cables, braids, heat shrink, sleeves, fasteners, consumables, etc. are subject to ±10% variation or actuals at billing, depending on packing/spool conditions.',
      'For chemicals/DG goods, please check the shelf-life column carefully before ordering.',
      'All quotes and orders are NCNR – Non-Cancellable and Non-Returnable.',
      'All product parts are subject to stock availability.',
      'Any increase or decrease in quoted quantity will affect the unit cost.',
      'All parts are subject to 100% re-stocking charges.',
      'Prices are quoted based on current USD/EUR/GBP to INR conversion. Any fluctuation beyond 2% at the time of PO will impact the unit cost.',
      'Orders below ₹3,000/- will attract a delivery charge of ₹300/- in the invoice.',
      'Purchase Orders must be placed in the name of:\nNGP SOLUTIONS, JR Urbania Prime, #969 & 970, Lingapura Village, Anekal Taluk, Bangalore – 562106 | GST : 29BCWPD5012J1ZC',
      'Payment Terms : Terms will vary depending on the customer.',
      'Generally, the company approves Net 30 terms subject to finance approval.',
      'As per company policy, for any new customers, 100% advance payment is required until either: The transaction value reaches ₹10 lakhs, or The customer submits 2 years of audited financial statements for review and approval of credit terms.'
    ];
  }

  /**
   * Export Supply Sense data as custom NGP Quotation Form in Word (.docx) format matching the quotation template
   */
  async exportToNgpQuotationDocx(
    rows: any[],
    fileName: string = 'NGP_Quotation.docx',
    options: {
      reviewerName?: string;
      dateStr?: string;
      projectName?: string;
      customerName?: string;
      buyerName?: string;
      buyerEmail?: string;
      buyerPhone?: string;
      quotationNo?: string;
    } = {}
  ): Promise<void> {
    if (!rows || !rows.length) {
      console.warn('No data available to export.');
      return;
    }

    const reviewerName = (options.reviewerName || 'KAVYA R').trim().toUpperCase();
    const now = new Date();
    const dateStr = options.dateStr || (() => {
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();

    const validUntilDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const validUntilStr = (() => {
      const dd = String(validUntilDate.getDate()).padStart(2, '0');
      const mm = String(validUntilDate.getMonth() + 1).padStart(2, '0');
      const yyyy = validUntilDate.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    })();

    const quotationNo = options.quotationNo || `NGP/QTN/${now.getFullYear()}/${Math.floor(100000 + Math.random() * 900000)}`;
    const projectName = options.projectName || '';
    const customerName = options.customerName || '';
    const buyerName = options.buyerName || '';
    const buyerEmail = options.buyerEmail || '';
    const buyerPhone = options.buyerPhone || '';

    let subtotal = 0;
    rows.forEach(r => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const rowTotal = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);
      subtotal += rowTotal;
    });

    const cgst = subtotal * 0.09;
    const sgst = subtotal * 0.09;
    const grandTotal = subtotal + cgst + sgst;

    const logoBytes = this.base64ToUint8Array(NGP_LOGO_BASE64);

    // Header Table (Logo + Metadata Box) on every page
    const headerTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [6006, 5000],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            // Left Cell: Logo
            new TableCell({
              width: { size: 6006, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: logoBytes.length > 0 ? [
                    new ImageRun({
                      data: logoBytes,
                      transformation: { width: 140, height: 75 },
                      type: 'jpg'
                    })
                  ] : [
                    new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 28, color: '002060', font: 'Calibri' })
                  ]
                })
              ]
            }),
            // Right Cell: Metadata Box with QUOTATION Title
            new TableCell({
              width: { size: 5000, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 50, bottom: 50, left: 80, right: 80 },
              children: [
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'Doc. Reference : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: 'NGP-MKT-QF-02', size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'Rev            : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: '02', size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({ text: 'Date           : ', bold: true, size: 15, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: dateStr, size: 15, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  border: { top: { style: BorderStyle.SINGLE, size: 1, color: '94A3B8' } },
                  spacing: { before: 40, after: 15 },
                  children: [
                    new TextRun({ text: 'QUOTATION', bold: true, size: 24, font: 'Calibri', color: '334155' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // Footer Table (Company info + Page number)
    const footerTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [9356, 1650],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 9356, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'JR URBANIA PRIME, # 969 & 970, LINGAPURA VILLAGE, ANEKAL TALUK, BANGALORE – 562106 | Email: sales@vigroup.in | Ph# 702617646',
                      size: 12,
                      font: 'Calibri',
                      color: '64748B'
                    })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 1650, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: 'Page ', size: 14, font: 'Calibri', color: '64748B' }),
                    new TextRun({ children: [PageNumber.CURRENT], size: 14, font: 'Calibri', color: '64748B' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 1. Blue Bar Table
    const blueBarTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [4400, 3300, 3306],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 4400, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: `QUOTATION # ${quotationNo}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 3300, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({ text: `DATE : ${dateStr}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            }),
            new TableCell({
              width: { size: 3306, type: WidthType.DXA },
              shading: { fill: '002060' },
              margins: { top: 60, bottom: 60, left: 80, right: 80 },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE }
              },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: `QUOTE VALID : ${validUntilStr}`, bold: true, size: 15, color: 'FFFFFF', font: 'Calibri' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 2. Company & Customer Info Table (2 Columns)
    const detailsTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [5503, 5503],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            // Left Column: Seller info
            new TableCell({
              width: { size: 5503, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: [
                new Paragraph({
                  spacing: { after: 30 },
                  children: [
                    new TextRun({ text: 'TRASCCON INTERCONNECTIONS SYSTEMS PVT LTD', bold: true, size: 16, color: '002060', font: 'Calibri' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 40 },
                  children: [
                    new TextRun({
                      text: 'AAROHANA, # 19/3, SECOND FLOOR, SRINIVASA INDUSTRIAL ESTATE, KANNAKAPURA MAIN ROAD, KONANAKUNTE POST, BANGALORE – 560062',
                      size: 14,
                      color: '334155',
                      font: 'Calibri'
                    })
                  ]
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'GST: 29AADCT6132C1ZJ', bold: true, size: 15, color: '0F172A', font: 'Calibri' })
                  ]
                })
              ]
            }),
            // Right Column: Sales Agent & Customer Details
            new TableCell({
              width: { size: 5503, type: WidthType.DXA },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
                right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
              },
              margins: { top: 80, bottom: 80, left: 100, right: 100 },
              children: [
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'YOUR SALES AGENT : ', bold: true, size: 14, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: reviewerName, size: 14, font: 'Calibri', color: '002060', bold: true })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'EMAIL            : ', bold: true, size: 14, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: 'kavya.r@vigroup.in', size: 14, font: 'Calibri', color: '2563EB', underline: {} })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 35 },
                  children: [
                    new TextRun({ text: 'PHONE NO.        : ', bold: true, size: 14, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: '+91 9606048420', size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' } },
                  spacing: { before: 25, after: 15 },
                  children: [
                    new TextRun({ text: 'CUSTOMER REFERENCE : ', bold: true, size: 14, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: customerName || projectName, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'BUYER              : ', bold: true, size: 14, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerName, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  spacing: { after: 15 },
                  children: [
                    new TextRun({ text: 'EMAIL ID           : ', bold: true, size: 14, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerEmail, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                }),
                new Paragraph({
                  children: [
                    new TextRun({ text: 'PHONE NUMBER       : ', bold: true, size: 14, font: 'Calibri', color: '475569' }),
                    new TextRun({ text: buyerPhone, size: 14, font: 'Calibri', color: '1E293B' })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 3. Salutation Paragraph
    const introParagraph = new Paragraph({
      spacing: { before: 120, after: 120 },
      children: [
        new TextRun({
          text: 'Dear Mr. _______________ Thank you for your valuable RFQ and the opportunity to participate in your sourcing requirements. Based on your demands and specifications, we have carefully reviewed the requirements and worked out our most competitive pricing for your kind review and consideration. We look forward to your valuable feedback and the opportunity to support your business requirements.',
          size: 15,
          font: 'Calibri',
          color: '1E293B'
        })
      ]
    });

    // 4. Main Quotation Table (15 Columns - Exact Fixed Dimensions for A4 Printout)
    const headers = [
      { lines: ['SL', 'NO'], width: 380, align: AlignmentType.CENTER },
      { lines: ['CUSTOMER', 'REFERENCE'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['MANUFACTURER', 'PART NO.'], width: 1250, align: AlignmentType.LEFT },
      { lines: ['ALTERNATE', 'QUOTED'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['DESCRIPTION'], width: 1350, align: AlignmentType.LEFT },
      { lines: ['MANUFACTURER'], width: 1050, align: AlignmentType.LEFT },
      { lines: ['HSN', 'CODE'], width: 550, align: AlignmentType.CENTER },
      { lines: ['UOM'], width: 400, align: AlignmentType.CENTER },
      { lines: ['QTY'], width: 500, align: AlignmentType.RIGHT },
      { lines: ['MOQ/SPQ'], width: 550, align: AlignmentType.CENTER },
      { lines: ['UNIT', 'PRICE'], width: 720, align: AlignmentType.RIGHT },
      { lines: ['TOTAL'], width: 780, align: AlignmentType.RIGHT },
      { lines: ['LEAD', 'TIME'], width: 550, align: AlignmentType.CENTER },
      { lines: ['COO'], width: 400, align: AlignmentType.CENTER },
      { lines: ['REMARKS'], width: 456, align: AlignmentType.LEFT }
    ];

    const tableHeaderRow = new TableRow({
      tableHeader: true,
      cantSplit: true,
      children: headers.map(h => new TableCell({
        width: { size: h.width, type: WidthType.DXA },
        shading: { fill: '002060' },
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 40, bottom: 40, left: 15, right: 15 },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '001040' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '001040' }
        },
        children: h.lines.map(line => new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 0, after: 0, line: 190 },
          children: [
            new TextRun({ text: line, bold: true, size: 13, font: 'Calibri', color: 'FFFFFF' })
          ]
        }))
      }))
    });

    const tableDataRows = rows.map((r, idx) => {
      const qty = Number(r.quantity ?? r.projectQty ?? 0);
      const unitPrice = Number(r.unitPrice ?? 0);
      const total = (r.totalPrice !== undefined && r.totalPrice !== null && !isNaN(Number(r.totalPrice)))
        ? Number(r.totalPrice)
        : (qty * unitPrice);

      const cellValues = [
        { text: String(r.slNo ?? (idx + 1)), align: AlignmentType.CENTER },
        { text: String(r.partNumber ?? ''), align: AlignmentType.LEFT },
        { text: String(r.quotedPart ?? ''), align: AlignmentType.LEFT },
        { text: String(r.alternateQuoted ?? ''), align: AlignmentType.LEFT },
        { text: String(r.description ?? ''), align: AlignmentType.LEFT },
        { text: String(r.make ?? ''), align: AlignmentType.LEFT },
        { text: String(r.hsnCode ?? ''), align: AlignmentType.CENTER },
        { text: String(r.uom ?? 'EA'), align: AlignmentType.CENTER },
        { text: qty ? qty.toLocaleString('en-IN') : '0', align: AlignmentType.RIGHT },
        { text: String(r.moqSpq ?? ''), align: AlignmentType.CENTER },
        { text: unitPrice > 0 ? unitPrice.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', align: AlignmentType.RIGHT },
        { text: total > 0 ? total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', align: AlignmentType.RIGHT },
        { text: String(r.leadTime ?? ''), align: AlignmentType.CENTER },
        { text: String(r.coo ?? ''), align: AlignmentType.CENTER },
        { text: String(r.remarks ?? ''), align: AlignmentType.LEFT }
      ];

      return new TableRow({
        cantSplit: true,
        children: cellValues.map((cv, cIdx) => new TableCell({
          width: { size: headers[cIdx].width, type: WidthType.DXA },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 30, bottom: 30, left: 15, right: 15 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            left: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' },
            right: { style: BorderStyle.SINGLE, size: 1, color: 'CBD5E1' }
          },
          children: [
            new Paragraph({
              alignment: cv.align,
              spacing: { before: 0, after: 0, line: 200 },
              children: [
                new TextRun({ text: cv.text || '-', size: 13, font: 'Calibri', color: '000000' })
              ]
            })
          ]
        }))
      });
    });

    const quotationItemsTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: headers.map(h => h.width),
      rows: [tableHeaderRow, ...tableDataRows]
    });

    // 5. Financial Summary Table (Right-aligned, yellow highlight, fixed layout)
    const summaryBorder = { style: BorderStyle.SINGLE, size: 1, color: '000000' };
    const noBorder = { style: BorderStyle.NONE };

    const summaryTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [7606, 1700, 1700],
      borders: {
        top: noBorder,
        bottom: noBorder,
        left: noBorder,
        right: noBorder,
        insideHorizontal: noBorder,
        insideVertical: noBorder
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'Total :', bold: true, size: 16, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: subtotal > 0 ? subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', bold: true, size: 16, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'CGST @ 9%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: cgst > 0 ? cgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'SGST @ 9%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: sgst > 0 ? sgst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'IGST @ 18%', size: 15, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: '-', size: 15, font: 'Calibri', color: '000000' })] })]
            })
          ]
        }),
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 7606, type: WidthType.DXA },
              borders: { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 } })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ spacing: { before: 0, after: 0 }, children: [new TextRun({ text: 'Grand Total :', bold: true, size: 17, font: 'Calibri', color: '000000' })] })]
            }),
            new TableCell({
              width: { size: 1700, type: WidthType.DXA },
              shading: { fill: 'FFFF00' },
              margins: { top: 35, bottom: 35, left: 50, right: 50 },
              borders: { top: summaryBorder, bottom: summaryBorder, left: summaryBorder, right: summaryBorder },
              children: [new Paragraph({ alignment: AlignmentType.RIGHT, spacing: { before: 0, after: 0 }, children: [new TextRun({ text: grandTotal > 0 ? grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-', bold: true, size: 17, font: 'Calibri', color: '000000' })] })]
            })
          ]
        })
      ]
    });

    // 6. Quotation Notes Box
    const quotationNotesBox = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 11006, type: WidthType.DXA },
      columnWidths: [11006],
      borders: {
        top: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        left: { style: BorderStyle.SINGLE, size: 1, color: '64748B' },
        right: { style: BorderStyle.SINGLE, size: 1, color: '64748B' }
      },
      rows: [
        new TableRow({
          cantSplit: true,
          children: [
            new TableCell({
              width: { size: 11006, type: WidthType.DXA },
              margins: { top: 60, bottom: 400, left: 80, right: 80 },
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Quotation Notes:', bold: true, size: 16, font: 'Calibri', color: '002060', underline: {} })
                  ]
                })
              ]
            })
          ]
        })
      ]
    });

    // 7. Terms & Conditions
    const terms = this.getNgpTermsAndConditions();
    const termsParagraphs = [
      new Paragraph({
        pageBreakBefore: true,
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({ text: 'Delivery Terms:', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: 'Ex-Works / Standard Delivery Schedule as quoted.', size: 14, font: 'Calibri', color: '334155' })
        ]
      }),
      new Paragraph({
        spacing: { before: 60, after: 40 },
        children: [
          new TextRun({ text: 'Payment Terms:', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { after: 100 },
        children: [
          new TextRun({ text: 'Net 30 days subject to finance approval / 100% advance for new customers.', size: 14, font: 'Calibri', color: '334155' })
        ]
      }),
      new Paragraph({
        spacing: { before: 80, after: 60 },
        children: [
          new TextRun({ text: 'General Terms & Conditions:', bold: true, size: 17, font: 'Calibri', color: '000000', underline: {} })
        ]
      }),
      ...terms.map(term => new Paragraph({
        spacing: { after: 20 },
        children: [
          new TextRun({ text: '▪  ', bold: true, size: 13, font: 'Calibri', color: '002060' }),
          new TextRun({ text: term, size: 14, font: 'Calibri', color: '1E293B' })
        ]
      })),
      new Paragraph({
        spacing: { before: 120, after: 30 },
        children: [
          new TextRun({ text: 'For,', size: 15, font: 'Calibri', color: '1E293B' })
        ]
      }),
      new Paragraph({
        spacing: { after: 280 },
        children: [
          new TextRun({ text: 'NGP SOLUTIONS', bold: true, size: 17, font: 'Calibri', color: '002060' })
        ]
      }),
      new Paragraph({
        spacing: { after: 80 },
        children: [
          new TextRun({ text: 'Authorized Signatories', bold: true, size: 16, font: 'Calibri', color: '000000' })
        ]
      }),
      new Paragraph({
        spacing: { before: 80, after: 40 },
        children: [
          new TextRun({
            text: 'This is a system generated document and does not require manual signatories',
            italics: true,
            size: 14,
            font: 'Calibri',
            color: '64748B'
          })
        ]
      })
    ];

    // Build Word Document
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                orientation: PageOrientation.PORTRAIT
              },
              margin: {
                top: 450,
                bottom: 450,
                left: 450,
                right: 450
              }
            }
          },
          headers: {
            default: new Header({
              children: [headerTable]
            })
          },
          footers: {
            default: new Footer({
              children: [footerTable]
            })
          },
          children: [
            blueBarTable,
            new Paragraph({ spacing: { after: 60 } }),
            detailsTable,
            introParagraph,
            quotationItemsTable,
            new Paragraph({ spacing: { after: 60 } }),
            summaryTable,
            new Paragraph({ spacing: { after: 80 } }),
            quotationNotesBox,
            ...termsParagraphs
          ]
        }
      ]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName.endsWith('.docx') ? fileName : `${fileName}.docx`);
  }

  /**
   * Export general data to standard Word (.docx) document for other instances
   */
  async exportToDocx(
    data: any[],
    fileName: string = 'ExportedData.docx',
    title: string = 'Supply Sense Report'
  ): Promise<void> {
    if (!data || !data.length) {
      console.warn('No data available to export.');
      return;
    }

    const headers = Object.keys(data[0]);

    // Build header row
    const headerRow = new TableRow({
      tableHeader: true,
      cantSplit: true,
      children: headers.map(h => new TableCell({
        shading: { fill: '2563EB' },
        verticalAlign: VerticalAlign.CENTER,
        margins: { top: 50, bottom: 50, left: 30, right: 30 },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '93C5FD' }
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 0, after: 0 },
            children: [
              new TextRun({ text: h, bold: true, size: 13, font: 'Calibri', color: 'FFFFFF' })
            ]
          })
        ]
      }))
    });

    // Build data rows
    const dataRows = data.map((item, idx) => new TableRow({
      cantSplit: true,
      children: headers.map(h => {
        const val = item[h] !== undefined && item[h] !== null ? String(item[h]) : '';
        const isNum = !isNaN(Number(val)) && val.trim() !== '';
        return new TableCell({
          shading: { fill: idx % 2 === 0 ? 'FFFFFF' : 'F8FAFC' },
          verticalAlign: VerticalAlign.CENTER,
          margins: { top: 30, bottom: 30, left: 25, right: 25 },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            left: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' },
            right: { style: BorderStyle.SINGLE, size: 1, color: 'E2E8F0' }
          },
          children: [
            new Paragraph({
              alignment: isNum ? AlignmentType.RIGHT : AlignmentType.LEFT,
              spacing: { before: 0, after: 0, line: 200 },
              children: [
                new TextRun({ text: val || '-', size: 13, font: 'Calibri', color: '000000' })
              ]
            })
          ]
        });
      })
    }));

    const mainTable = new Table({
      layout: TableLayoutType.FIXED,
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [headerRow, ...dataRows]
    });

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              size: {
                orientation: PageOrientation.LANDSCAPE
              },
              margin: {
                top: 500,
                bottom: 500,
                left: 500,
                right: 500
              }
            }
          },
          headers: {
            default: new Header({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: title, bold: true, size: 18, font: 'Calibri', color: '1E293B' }),
                    new TextRun({ text: `   |   Generated: ${new Date().toLocaleString()}`, size: 13, font: 'Calibri', color: '64748B' })
                  ]
                })
              ]
            })
          },
          footers: {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: 'Supply Sense Report  |  Page ', size: 13, font: 'Calibri', color: '94A3B8' }),
                    new TextRun({ children: [PageNumber.CURRENT], size: 13, font: 'Calibri', color: '94A3B8' })
                  ]
                })
              ]
            })
          },
          children: [
            new Paragraph({
              spacing: { after: 80 },
              children: [
                new TextRun({ text: title, bold: true, size: 22, font: 'Calibri', color: '1E293B' })
              ]
            }),
            new Paragraph({
              spacing: { after: 120 },
              children: [
                new TextRun({ text: `Exported on ${new Date().toLocaleString()}`, size: 14, font: 'Calibri', color: '64748B' })
              ]
            }),
            mainTable
          ]
        }
      ]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, fileName.endsWith('.docx') ? fileName : `${fileName}.docx`);
  }
}

